/**
 * The access-review sheet (ops-side, path /ops → Access review).
 *
 * Vlad, 2026-07-21: nobody wants to own the nurse list — not Alex, not
 * Helen, not Owen — so it rots, and a rotting access list at a nursing
 * home still has last year's agency staff on it. This screen renders the
 * recurring "who should still have access?" table: quarterly to each
 * home's administrator, monthly to Alex.
 *
 * The last column is deliberately empty. Everything to its left is what we
 * know; that column is what only the home knows.
 *
 * Rendering only — the send is not wired. Email needs the Resend key and a
 * verified sending domain (Alex owes both), and a Resend key is a fatal
 * startup error in synthetic mode by design. "Copy as a table" and the CSV
 * download are the interim hand-off.
 *
 * The decision column is tappable (Keep / Remove, 2026-07-21 review): the
 * reviewer marks each row on screen and both exports carry the marks, so
 * the answer travels back in the same table it was asked in. Marks live in
 * screen state only — this surface produces the artefact, it does not
 * enforce access.
 */
import { useEffect, useState } from 'preact/hooks';
import type { ApiClient } from '../api.js';
import { opsCopy, sharedCopy } from '../copy.js';
import { accessReviewCsv, type AccessReview as Report } from '../../core/access-review.js';

/**
 * ⭐ T2075 M5 — THE WIRE TYPE IS THE CORE'S, imported rather than mirrored. Two
 * local mirrors (`ReviewNurse`, `ReviewHome`) used to live here so a Report was
 * assignable to the `AccessReview` the CSV builder takes; when the review gained
 * the role, the group, the R-9 flag and the acts section they would have had to
 * gain the same fields for the same reason, and a mirror that must track its
 * source field for field is the source. The screen still renders only the
 * columns it did (the kiosk's Nursing homes screen is the operators' reader).
 */

export type AccessReviewProps = { api: ApiClient; passcode: string; onBack: () => void };

type Decision = 'keep' | 'remove';

export function AccessReview({ api, passcode, onBack }: AccessReviewProps) {
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Keyed by nurse email (unique across homes). Screen state only.
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ report: Report; csv: string }>('/api/ops/access-review', {
        'x-ops-passcode': passcode,
      });
      if (res.kind === 'network-error') {
        setError(sharedCopy.networkError);
        return;
      }
      if (res.status !== 200) {
        setError(sharedCopy.genericError);
        return;
      }
      setReport(res.body.report);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleDecision(email: string, next: Decision): void {
    setDecisions((prev) => {
      const current = prev[email];
      const copy = { ...prev };
      if (current === next) {
        delete copy[email]; // tapping the active mark clears it
      } else {
        copy[email] = next;
      }
      return copy;
    });
  }

  function buildCsv(): string {
    if (!report) return '';
    return accessReviewCsv(report, (_homeName, email) => {
      const d = decisions[email];
      return d === 'keep' ? opsCopy.accessReviewKeep : d === 'remove' ? opsCopy.accessReviewRemove : '';
    });
  }

  async function copyCsv(): Promise<void> {
    try {
      await navigator.clipboard.writeText(buildCsv());
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function downloadCsv(): void {
    if (!report) return;
    const blob = new Blob([buildCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-review-${report.generatedAt.slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (error) {
    return (
      <div class="screen ops-screen">
        <p class="field-error" role="alert">
          {error}
        </p>
        <button type="button" class="btn btn--secondary" onClick={onBack}>
          {opsCopy.accessReviewBack}
        </button>
      </div>
    );
  }

  if (!report) {
    return (
      <p class="loading-notice" role="status">
        {sharedCopy.loading}
      </p>
    );
  }

  return (
    <div class="screen ops-screen">
      <h1>{opsCopy.accessReviewTitle}</h1>
      <p class="notice">{opsCopy.accessReviewBlurb}</p>

      <div class="action-row">
        <button type="button" class="btn btn--secondary" onClick={() => void copyCsv()}>
          {opsCopy.accessReviewCopyCsv}
        </button>
        <button type="button" class="btn btn--secondary" onClick={downloadCsv}>
          {opsCopy.accessReviewDownload}
        </button>
        <button type="button" class="btn btn--link" onClick={onBack}>
          {opsCopy.accessReviewBack}
        </button>
      </div>
      {copied && (
        <p class="copy-confirm" role="status">
          {opsCopy.accessReviewCopied}
        </p>
      )}

      {report.homes.map((home) => (
        // ⛔ T1146 M2: keyed on `homeId`, not `homeName`. Until M2 the producer
        // merged two same-named homes into ONE entry, so the name was unique by
        // accident; M2 emits two, and a duplicate React key makes Preact reuse
        // the wrong section's DOM — the wrong roster under the wrong heading, on
        // an Article 9 recertification screen.
        <section key={home.homeId} class="access-review-home">
          <h2>{home.homeName}</h2>
          <table class="ops-table">
            <thead>
              <tr>
                <th>{opsCopy.accessReviewColNurse}</th>
                <th>{opsCopy.accessReviewColEmail}</th>
                <th>{opsCopy.accessReviewColLastUsed}</th>
                <th>{opsCopy.accessReviewColDevices}</th>
                {/* ⛔ T788 M4. Deactivated nurses now APPEAR in this report — the
                    query used to hide them, which meant a removal could only be
                    trusted, never confirmed. But showing her WITHOUT this column
                    is worse than hiding her: her row is byte-identical to a
                    current nurse's whenever the other values coincide, so the
                    reviewer re-certifies access that is already gone. The CSV
                    gained the same column for the same reason. */}
                <th>{opsCopy.accessReviewColAccess}</th>
                <th>{opsCopy.accessReviewColFlag}</th>
                <th>{opsCopy.accessReviewColDecision}</th>
              </tr>
            </thead>
            <tbody>
              {home.nurses.map((n) => (
                <tr key={n.email}>
                  <td>{n.displayName}</td>
                  <td>{n.email}</td>
                  <td>{n.lastActiveAt === null ? opsCopy.accessReviewNever : n.lastActiveAt.slice(0, 10)}</td>
                  <td>{n.deviceCount}</td>
                  <td>{n.active ? opsCopy.accessReviewAccessYes : opsCopy.accessReviewAccessNo}</td>
                  <td>{n.dormant ? opsCopy.accessReviewDormantFor(report.dormantAfterDays) : ''}</td>
                  <td class="access-review-decision">
                    <button
                      type="button"
                      class={
                        decisions[n.email] === 'keep'
                          ? 'decision-btn decision-btn--keep decision-btn--active'
                          : 'decision-btn decision-btn--keep'
                      }
                      aria-pressed={decisions[n.email] === 'keep'}
                      aria-label={`${opsCopy.accessReviewKeep}: ${n.displayName}`}
                      onClick={() => toggleDecision(n.email, 'keep')}
                    >
                      ✓ {opsCopy.accessReviewKeep}
                    </button>
                    <button
                      type="button"
                      class={
                        decisions[n.email] === 'remove'
                          ? 'decision-btn decision-btn--remove decision-btn--active'
                          : 'decision-btn decision-btn--remove'
                      }
                      aria-pressed={decisions[n.email] === 'remove'}
                      aria-label={`${opsCopy.accessReviewRemove}: ${n.displayName}`}
                      onClick={() => toggleDecision(n.email, 'remove')}
                    >
                      ✗ {opsCopy.accessReviewRemove}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
